import { useState, useMemo } from 'react';
import { Product as ProductDTO } from '@setvi/shared/interfaces';
import { SBase } from '@setvi/shared/components';
import { EmbeddedCatalog } from 'componentsV2/catalog';
import { QuoteDTO } from '@setvi/shared/services/react-query';

interface AddFromCatalogDialogProps {
  onClose: () => void;
  parentId?: string | null;
  /** Quote data - required when used outside QuoteProvider context */
  quote?: QuoteDTO;
  /** Function to add products - required when used outside QuoteProvider context */
  addProducts?: (params: {
    products: ProductDTO[];
    parentId: string | null;
  }) => void;
}

/**
 * Dialog for adding products from catalog to quote
 * Uses the shared EmbeddedCatalog component
 *
 * Can be used either within QuoteProvider context or with explicit quote/addProducts props
 */
export const AddFromCatalogDialog = ({
  onClose,
  parentId,
  quote,
  addProducts
}: AddFromCatalogDialogProps) => {
  const [selectedProducts, setSelectedProducts] = useState<ProductDTO[]>([]);

  const handleProductSelect = (isChecked: boolean, products: ProductDTO[]) => {
    if (isChecked) {
      // Add products to selection
      setSelectedProducts(prev => {
        const newProducts = products.filter(
          product => !prev.some(p => p._id === product._id)
        );
        return [...prev, ...newProducts];
      });
    } else {
      // Remove products from selection
      setSelectedProducts(prev =>
        prev.filter(p => !products.some(product => p._id === product._id))
      );
    }
  };

  const handleConfirm = () => {
    if (selectedProducts.length > 0 && addProducts) {
      addProducts({
        products: selectedProducts,
        parentId: parentId || null
      });
      onClose();
    }
  };

  // Get customer from quote for pricing
  // Memoize to prevent unnecessary re-renders and price clearing
  const selectedCustomer = useMemo(() => {
    if (!quote?.account) return undefined;

    return {
      _id: quote.account._id,
      accountNumber: quote.account.accountNumber,
      name: quote.account.name
    } as any;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    quote?.account?._id,
    quote?.account?.accountNumber,
    quote?.account?.name
  ]);

  return (
    <SBase
      header={{
        title: 'Add Products from Catalog',
        onClose
      }}
      footer={{
        onConfirm: handleConfirm,
        onClose,
        confirmLabel: `Add Products (${selectedProducts.length})`,
        closeLabel: 'Cancel',
        disableConfirm: selectedProducts.length === 0 || !addProducts
      }}
      width="90vw"
      height="85vh">
      <EmbeddedCatalog
        selectedCustomer={selectedCustomer}
        selectedProducts={selectedProducts}
        onProductSelect={handleProductSelect}
      />
    </SBase>
  );
};
